import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'jiaoyou');
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setVisibleCount(6);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);
  const visibleArticles = categoryArticles.slice(0, visibleCount);
  const hasMore = visibleCount < categoryArticles.length;

  const popularArticles = articles
    .filter((a) => a.category !== activeSlug)
    .sort(() => 0.5 - Math.random())
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="mt-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {currentCategory.name}
              </h1>
              <p className="mt-2 text-foreground-500 text-sm md:text-base">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              {visibleArticles.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                    {visibleArticles.map((article) => (
                      <ArticleCard
                        key={article.id}
                        slug={article.slug}
                        title={article.title}
                        excerpt={article.excerpt}
                        category={article.category}
                        categoryName={currentCategory.name}
                        author={article.author}
                        publishDate={article.publishDate}
                        readTime={article.readTime}
                        imageUrl={article.imageUrl}
                      />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={() => setVisibleCount((prev) => prev + 6)}
                        className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        加载更多文章
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-400 text-sm">该分类暂无文章</p>
                </div>
              )}
            </div>

            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <h3 className="text-base font-semibold text-foreground-900 mb-4">
                  热门推荐
                </h3>
                <div className="flex flex-col gap-4">
                  {popularArticles.map((article) => {
                    const cat = categories.find((c) => c.id === article.category);
                    return (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                            {article.title}
                          </h4>
                          <span className="text-xs text-foreground-400 mt-1 block">
                            {cat?.name}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}